import { createContext, useCallback, useEffect, useMemo, useContext, type ReactNode } from 'react';
import { useAuth as useOidcAuth } from 'react-oidc-context';
import { LegacyAuthContext } from './AuthProvider';
import { onUnauthorized } from '@shared/lib/authEvents';
import type { User, AuthTokens, LoginRequest } from '@shared/types';

export interface UnifiedAuthState {
  user: User | null;
  tokens: AuthTokens | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (request: LoginRequest) => Promise<void>;
  loginSso: () => Promise<void>;
  logout: () => void;
  authSource: 'legacy' | 'oidc' | null;
}

export const UnifiedAuthContext = createContext<UnifiedAuthState | null>(null);

function mapOidcUser(oidcUser: { profile: Record<string, unknown> }): User {
  const p = oidcUser.profile;
  return {
    id: (p.sub as string) ?? '',
    userName: (p.preferred_username as string) ?? (p.email as string) ?? '',
    roleId: '',
  };
}

export function UnifiedAuthProvider({ children }: { children: ReactNode }) {
  const legacy = useContext(LegacyAuthContext);
  const oidc = useOidcAuth();
  const { signinRedirect, signoutRedirect } = oidc;

  const legacyAuthenticated = legacy?.isAuthenticated ?? false;
  const oidcAuthenticated = oidc.isAuthenticated;

  const authSource = legacyAuthenticated ? 'legacy' : oidcAuthenticated ? 'oidc' : null;

  const user = useMemo<User | null>(() => {
    if (legacyAuthenticated && legacy?.user) return legacy.user;
    if (oidcAuthenticated && oidc.user) return mapOidcUser(oidc.user);
    return null;
  }, [legacyAuthenticated, oidcAuthenticated, legacy?.user, oidc.user]);

  const tokens = useMemo<AuthTokens | null>(() => {
    if (legacyAuthenticated && legacy?.tokens) return legacy.tokens;
    if (oidcAuthenticated && oidc.user) {
      return {
        accessToken: oidc.user.access_token,
        refreshToken: oidc.user.refresh_token ?? '',
        expiresAt: (oidc.user.expires_at ?? 0) * 1000,
      };
    }
    return null;
  }, [legacyAuthenticated, oidcAuthenticated, legacy?.tokens, oidc.user]);

  const isLoading = (legacy?.isLoading ?? true) || oidc.isLoading;

  const login = useCallback(
    async (request: LoginRequest) => {
      if (!legacy) throw new Error('Legacy auth not available');
      await legacy.loginLegacy(request);
    },
    [legacy],
  );

  const loginSso = useCallback(async () => {
    await signinRedirect();
  }, [signinRedirect]);

  const logout = useCallback(() => {
    legacy?.logout();
    if (authSource === 'oidc') {
      void signoutRedirect();
    }
  }, [authSource, signoutRedirect, legacy]);

  useEffect(() => onUnauthorized(logout), [logout]);

  const value = useMemo<UnifiedAuthState>(
    () => ({
      user,
      tokens,
      isAuthenticated: !!user,
      isLoading,
      login,
      loginSso,
      logout,
      authSource,
    }),
    [user, tokens, isLoading, login, loginSso, logout, authSource],
  );

  return <UnifiedAuthContext.Provider value={value}>{children}</UnifiedAuthContext.Provider>;
}
